import { styled } from "@mui/material/styles";
import { Box, Typography, Button, Skeleton, FormControlLabel } from "@mui/material";
import { Close } from "@mui/icons-material";

// ==============================
// GeneralSettings Component Styles
// ==============================
export const WhiteContainerWrapper = styled(Box)({
  marginTop: "24px",
});

// Header Components
export const HeaderContainerGeneralSettings = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  gap: "4px",
});

export const HeaderTitleGeneralSettings = styled(Typography)({
  fontSize: 20,
  fontWeight: 700,
  color: "#6B7280",
  cursor: "pointer",
});

export const HeaderDescription = styled(Typography)({
  marginTop: "16px",
  textAlign: "start",
  fontSize: "16px",
  fontWeight: 400,
  color: "#ADBAC7",
  maxWidth: "550px",
});

// Tabs Components
export const TabsContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  gap: "4px",
});

export const TabButton = styled(Button)<{ active: boolean }>(({ active }) => ({
  cursor: "pointer",
  padding: "8px 16px",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 700,
  backgroundColor: active ? "#EEEBFF" : "white",
  color: active ? "#364152" : "#8E98A1",
  "&:hover": {
    backgroundColor: active ? "#EEEBFF" : "white",
  },
}));

// Section Components
export const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "24px",
});

export const SectionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 700,
  color: "#435363",
});

export const SectionSubtitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 700,
  color: "#364152",
});

// Button Components
export const EditButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "87px",
  height: "41px",
  backgroundColor: "#5B3FFF",
  borderRadius: "8px",
  color: "#FFFFFF",
  fontSize: "14px",
  fontWeight: 700,
  "&:hover": {
    backgroundColor: "#4a2fd9",
  },
});

export const CancelButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "87px",
  height: "41px",
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  color: "#5B3FFF",
  fontSize: "14px",
  fontWeight: 700,
  border: "1px solid #5B3FFF",
});

// Image Gallery Components
export const GalleryContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  paddingTop: "24px",
});

export const ImageGrid = styled(Box)({
  position: "relative",
  display: "flex",
  flexWrap: "wrap",
  gap: "7px",
});

export const ImageGridItem = styled(Box)({
  position: "relative",
  flex: 1,
});

export const PropertyImageGeneralSettings = styled("img")({
  width: "220px",
  height: "170px",
  borderRadius: "4px",
});

// Form Components
export const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

export const FormLabel = styled(Typography)<{ disabled?: boolean }>(
  ({ disabled }) => ({
    fontSize: "16px",
    fontWeight: 700,
    color: "#364152",
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  })
);

export const FormTextArea = styled("textarea")<{ disabled?: boolean }>(
  ({ disabled }) => ({
    maxWidth: "507px",
    fontSize: "16px",
    fontWeight: 400,
    color: "#435363",
    outline: "none",
    backgroundColor: "white",
    height: "100px",
    border: "1px solid #EBECED",
    borderRadius: "8px",
    padding: "10px",
    marginTop: "16px",
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "auto" : "auto",
    resize: "vertical",
    overflow: "auto",
    cursor: disabled ? "default" : "text",
  })
);
export const FormInput = styled("input")<{ disabled?: boolean }>(
  ({ disabled }) => ({
    maxWidth: "507px",
    fontSize: "16px",
    fontWeight: 400,
    color: "#435363",
    outline: "none",
    backgroundColor: "white",
    height: "41px",
    border: "1px solid #EBECED",
    borderRadius: "8px",
    padding: "10px",
    marginTop: "16px",
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "none" : "auto",
  })
);

export const FormDivider = styled(Box)({
  paddingTop: "36px",
  marginBottom: "24px",
  borderBottom: "1px solid #EBECED",
});

// Base Input Container - Reusable styled component
interface BaseInputContainerProps {
  marginTop?: string;
  minWidth?: string;
}

export const BaseInputContainer = styled(Box)<BaseInputContainerProps>(({ marginTop = "16px", minWidth }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  maxWidth: "507px",
  minWidth: minWidth,
  border: "1px solid #EBECED",
  backgroundColor: "white",
  height: "41px",
  borderRadius: "8px",
  padding: "10px",
  marginTop: marginTop,
  position: "relative"
}));

// Specific Input Containers using the base
export const DiscountInputContainer = styled(BaseInputContainer)({});
export const FeesInputContainer = styled(BaseInputContainer)({});
export const AvailabilityInputContainer = styled(BaseInputContainer)({});
export const CommissionInputContainer = styled(BaseInputContainer)({});

// Time Input Containers using the base with position relative
export const TimeInputContainerCheckIn = styled(BaseInputContainer)({
  position: "relative",
  width: "25%",
});
export const TimeInputContainerCheckOut = styled(BaseInputContainer)({
  position: "relative",
  width: "25%",
});

export const TimeIcon = styled(Box)({
  width: "20px",
  height: "20px",
  color: "#6B7280",
  marginRight: "8px",
});

export const TimeInput = styled("input")({
  flex: 1,
  height: "100%",
  outline: "none",
  border: "none",
  backgroundColor: "transparent",
  fontSize: "14px",
  color: "#364152",
  "&:disabled": {
    color: "#435363",
    fontSize: "16px",
    opacity: 0.5,
  },
});

// Facilities Components
export const FacilitiesContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  paddingTop: "24px",
  gap: "16px",
});

export const FacilityItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

export const FacilityIcon = styled(Box)({
  width: "24px",
  height: "24px",
});

export const FacilityText = styled(Typography)({
  fontSize: "16px",
  fontWeight: 400,
  color: "#435363",
});

// Modal Styles
export const ModalMainContainer = styled("div")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: "938px",
  maxHeight: "1007px",
  backgroundColor: "#fff",
  paddingBlock: "24px",
});

export const ModalContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
  paddingInline: "90px",
});

export const ModalCloseIcon = styled(Close)({
  top: "24px",
  right: "24px",
  cursor: "pointer",
  fill: "#292D32",
});

export const ModalUpperSection = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginTop: "61px",
  width: "100%",
});

export const ModalTextSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
  gap: "15px",
});

export const ModalMainTitle = styled("span")({
  fontSize: "16px",
  fontWeight: "700",
  color: "#000000",
});

export const ModalDescription = styled("span")({
  fontSize: "14px",
  fontWeight: "400",
  color: "#261B6A",
  marginBottom: "15px",
});

export const ModalChangeButton = styled("span")({
  fontSize: "14px",
  fontWeight: "400",
  color: "#5B3FFF",
  cursor: "pointer",
  borderRadius: "80px",
  paddingBlock: "12px",
  paddingInline: "26px",
  border: "1px solid #5B3FFF",
});

export const ModalImage = styled("div")({
  borderRadius: "8px",
  width: "264px",
  height: "236px",
});

export const ModalLowerSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
});

export const ModalErrorText = styled("span")({
  fontSize: "12px",
  fontWeight: "400",
  color: "#E85B5B",
  marginTop: "24px",
});

// ==============================
// Time Modal Styles
// ==============================

export const TimeModalContainer = styled("div")<{
  isRTL: boolean;
  isCheckOut?: boolean;
}>(({ isRTL, isCheckOut }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  justifyContent: "center",
  position: "absolute",
  top: isRTL ? "60%" : "65%",
  left: isRTL ? undefined : "36%",
  right: isRTL ? "21%" : undefined,
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: "282px",
  maxHeight: "162px",
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "24px",
  boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.08)",
  ...(isCheckOut && {
    top: isRTL ? "110%" : "115%",
  }),
}));

export const TimeModalTitle = styled("h2")({
  fontSize: "12px",
  fontWeight: "500",
  color: "#435363",
  textAlign: "center",
});

export const TimeModalContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

// New radio button styles for the time modal
export const TimeOptionGroup = styled("div")({
  display: "flex",
  gap: "24px",
  justifyContent: "center",
  alignItems: "center",
});

export const TimeOptionLabel = styled("label")({
  display: "flex",
  alignItems: "center",
  textAlign: "center",
  gap: "8px",
  cursor: "pointer",
});

export const TimeOptionText = styled("span")({
  fontSize: "12px",
  fontWeight: "500",
  color: "#435363",
  transform: "translateY(2px)",
});

export const TimeOptionRadio = styled("input")({
  width: "26px",
  height: "26px",
  accentColor: "#5B3FFF",
  cursor: "pointer",
});

export const TimePickerContainer = styled("div")<{ isRTL: boolean }>(
  ({ isRTL }) => ({
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    flexDirection: isRTL ? "row" : "row-reverse",
  })
);

export const TimeInputField = styled("input")({
  width: "60px",
  height: "40px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: "#F5F5F5",
  textAlign: "center",
  fontSize: "24px",
  fontWeight: "500",
  color: "#1D1B20",
  "&:focus": {
    outline: "1px solid #5B3FFF",
    borderColor: "#5B3FFF",
  },
  "&::placeholder": {
    color: "#1D1B20",
  },
});

export const TimeSeparator = styled("span")({
  fontWeight: 400,
  fontSize: "28px",
  transform: "translateY(-10px)",
  color: "#364152",
});

export const TimeLabel = styled("span")({
  fontSize: "12px",
  fontWeight: "400",
  color: "#6B7280",
  marginTop: "4px",
  textAlign: "center",
});

export const TimeInputWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  gap: "3px",
});

export const TimeUnitLabel = styled("span")({
  fontSize: "10px",
  color: "#49454F",
  fontWeight: "400",
  alignSelf: "start",
});

// ==============================
// DeactivateModal Component Styles
// ==============================

export const ModalContainer = styled("div")({
  width: "689px",
  backgroundColor: "rgba(255, 255, 255, 1)",
  color: "#261B6A",
  boxShadow: "0px 4px 24px rgba(0,0,0,0.08)",
  padding: "24px",
  borderRadius: "16px",
});

export const ModalWrapper = styled("div")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
});

// Modal Gallery Components
export const ModalGalleryContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  marginTop: "24px",
});

export const ModalImageGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(6, 120px)",
  gap: "7px",
  width: "fit-content",
  marginBottom: "7px",
  "&:last-child": {
    marginBottom: 0,
  },
});

export const ModalImageGridItem = styled(Box)<{
  isSelected: boolean;
  isRTL: boolean;
}>(({ isSelected, isRTL }) => ({
  position: "relative",
  width: "120px",
  height: "120px",
  borderRadius: "4px",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    "& .more-icon": {
      opacity: 1,
      backgroundColor: isSelected ? "#5B3FFF" : "rgba(0, 0, 0, 0.7)",
    },
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  "& .add-icon": {
    width: "100%",
    height: "100%",
    borderRadius: "4px",
    border: "1px dashed #E5E7EB",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      borderColor: "#5B3FFF",
    },
  },
  "& .more-icon": {
    position: "absolute",
    top: "8px",
    left: "8px",
    width: "32px",
    height: "32px",
    padding: "4px",
    backgroundColor: isSelected ? "#5B3FFF" : "rgba(0, 0, 0, 0.5)",
    borderRadius: "4px",
    cursor: "pointer",
    opacity: 1,
    transition: "all 0.2s ease-in-out",
    color: "#FFFFFF",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      width: "24px",
      height: "24px",
      fill: "#FFFFFF",
    },
  },
  "& .image-menu": {
    position: "absolute",
    top: "50px",
    right: isRTL ? "0" : "70px",
    left: isRTL ? "70px" : "0",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    minWidth: "268px",
    padding: "8px",
  },
  "& .menu-item": {
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
    fontSize: "14px",
    color: "#000000",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
    "&.delete": {
      color: "#E85B5B",
    },
  },
}));

export const ModalGalleryContainerScroll = styled(Box)({
  overflowY: "auto",
  maxHeight: "250px",
  scrollbarWidth: "thin",
});

// ==============================
// Pricing Section PropertySetting
// ==============================

export const CommissionSettingsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: "12px",
  alignItems: "start",
  justifyContent: "center",
  maxWidth: "512px",
  width: "100%",
  backgroundColor: "#5B3FFF14",
  borderRadius: "12px",
  gap: "12px",
  marginTop: "24px",
});

export const CommissionSettingsHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

export const CommissionSettingsTitle = styled("span")({
  fontSize: "12px",
  fontWeight: 700,
  color: "#000000",
});

export const CommissionSettingsDescription = styled("span")({
  maxWidth: "512px",
  fontSize: "10px",
  color: "#000000B2",
  fontWeight: 400,
  textAlign: "start",
  display: "flex",
  alignItems: "start",
  justifyContent: "start",
  width: "100%",
});

export const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

export const CustomInput = styled("input")<{ disabled?: boolean }>(
  ({ disabled }) => ({
    flex: 1,
    height: "100%",
    outline: "none",
    border: "none",
    backgroundColor: "transparent",
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? "none" : "auto",
  })
);

export const InputIcon = styled("span")({
  fontSize: "16px",
});

export const SmallInputIcon = styled("span")({
  fontSize: "12px",
});

export const DiscountDescription = styled("span")({
  fontSize: "10px",
  color: "#B4BAC1",
  fontWeight: 400,
  marginTop: "8px",
});

// Error Box for form validation messages
export const ErrorBox = styled(Box)({
  position: "absolute",
  top: "100%",
  left: 0,
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
});

// Availability Settings Section Styled Components
export const RelativeContainer = styled(Box)({
  position: "relative",
});

export const CheckboxGroupContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

export const DayRowContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "8px 0",
});

export const DayInputGroup = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

export const ZeroMarginFormControlLabel = styled(FormControlLabel)({
  margin: 0,
});

// FormControlLabel with fixed width for min nights section
export const FixedWidthFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  width: "132px",
});

// CustomSelect with max width styling
export const StyledCustomSelect = styled(Box)({
  maxWidth: "507px",
  "& .MuiPaper-root": {
    maxHeight: "250px",
  },
});

// Wide AvailabilityInputContainer for min nights per week section
export const WideAvailabilityInputContainer = styled(BaseInputContainer)({
  minWidth: "300px",
  marginTop: "0px"
});

// ==============================
// CheckInSelectionModal Component Styles
// ==============================

export const CloseIconStyled = styled("div")<{ isRTL: boolean }>(
  ({ isRTL }) => ({
    width: "12px",
    height: "12px",
    position: "absolute",
    top: "20px",
    left: isRTL ? "20px" : undefined,
    right: isRTL ? undefined : "20px",
    cursor: "pointer",
    "& svg": {
      width: "100%",
      height: "100%",
    },
  })
);

// ==============================
// CommissionSection Component Styles
// ==============================

export const ChannelIconStyled = styled("div")({
  width: "24px",
  height: "24px",
  "& svg": {
    width: "100%",
    height: "100%",
  },
});

export const DollarIconStyled = styled("div")({
  width: "12px",
  height: "12px",
  "& svg": {
    width: "100%",
    height: "100%",
  },
});

// ButtonContainerStyled already exists above, removing duplicate

// ==============================
// ImageGalleryModal Component Styles
// ==============================

export const DividerStyled = styled("div")({
  width: "100%",
  marginTop: "61px",
});

export const DeleteMenuItemStyled = styled("div")<{ isDeleting: boolean }>(
  ({ isDeleting }) => ({
    opacity: isDeleting ? 0.7 : 1,
    cursor: isDeleting ? "not-allowed" : "pointer",
  })
);

export const AddImageItemStyled = styled("div")({
  cursor: "pointer",
});

export const FileInputStyled = styled("input")({
  display: "none",
});

export const UploadButtonContainerStyled = styled("div")({
  marginTop: "20px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
});

export const UploadButtonStyled = styled("div")({
  minWidth: "120px",
});

export const TextError = styled("span")({
  marginTop: "10px",
  color: "red",
  fontSize: "12px",
});

// ==============================
// ImageGridSkeleton Component Styles
// ==============================

export const SkeletonStyled = styled(Skeleton)({
  borderRadius: "4px",
  backgroundColor: "#f0f0f0",
  "&::after": {
    background: "linear-gradient(90deg, transparent, #e0e0e0, transparent)",
  },
});

// ==============================
// PropertySetting Index Component Styles
// ==============================

export const HiddenFormWatcher = styled("div")({
  display: "none",
});

export const ServiceIconStyled = styled("div")({
  width: "24px",
  height: "24px",
  "& svg": {
    width: "100%",
    height: "100%",
  },
});

export const ButtonContainerStyled = styled("div")({
  display: "flex",
  gap: "24px",
});

export const PropertyImageStyled = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

export const ErrorMessageStyled = styled("span")({
  color: "red",
  fontSize: "12px",
});

export const AmenitiesSectionStyled = styled("section")({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

export const AmenitiesContainerStyled = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  flexDirection: "row",
  gap: "10px",
  maxWidth: "917px",
  marginTop: "24px",
});

export const AmenityItemStyled = styled("div")({
  display: "flex",
  gap: "10px",
});

export const AmenityButtonStyled = styled("span")<{ isSelected: boolean }>(
  ({ isSelected }) => ({
    display: "flex",
    alignItems: "center",
    paddingInline: "16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "700",
    height: "29px",
    border: "1px solid #5B3FFF",
    backgroundColor: isSelected ? "#5B3FFF" : "#FFFFFF",
    color: isSelected ? "#FFFFFF" : "#5B3FFF",
    cursor: "pointer",
  })
);

export const AmenitiesDescriptionStyled = styled("span")({
  fontSize: "16px",
  fontWeight: "400",
  color: "#ADBAC7",
  maxWidth: "582px",
});

export const AmenitiesListContainerStyled = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

export const AmenityRowStyled = styled("div")<{ isSelected: boolean }>(
  ({ isSelected }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    height: "46px",
    backgroundColor: isSelected ? "#F9F7FF" : "#FFFFFF",
  })
);

export const AmenityNameStyled = styled("span")({
  fontSize: "14px",
  fontWeight: "700",
  color: "#331860",
});

export const AmenitiesErrorStyled = styled("div")<{ isRTL: boolean }>(
  ({ isRTL }) => ({
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "8px",
    textAlign: isRTL ? "right" : "left",
  })
);
